import type { ReactNode } from 'react';

import { SocialParseAnimation } from '@/components/RiveParseAnimation/RiveParseAnimation';
import type {
  TripImportProgressStage,
  TripImportProgressState,
  TripImportFoundPlacePreview,
} from '@/utils/tripImportProgressReducer';

import { PlaceSearchRowTypeIcon, type PlaceSearchRowSlotKind } from '@/components/PlaceSearchAutocomplete/PlaceSearchRowTypeIcon';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';

const FOUND_PREVIEW_LIMIT = 4;

export type TripImportProgressVisual = 'checklist' | 'socialScan';

export interface TripImportProgressStepProps {
  progress: TripImportProgressState;
  isUploading?: boolean;
  foundPlaces?: TripImportFoundPlacePreview[] | null;
  visual?: TripImportProgressVisual;
  onCancel: () => void;
}

export function TripImportProgressStep({
  progress,
  isUploading = false,
  foundPlaces = null,
  visual = 'checklist',
  onCancel,
}: TripImportProgressStepProps) {
  const isMobileViewport = useIsMobileViewport();
  const previews = foundPlaces ?? [];
  const visible = previews.slice(0, FOUND_PREVIEW_LIMIT);
  const extra = previews.length - visible.length;
  const foundCount = previews.length;
  const horizontalPad = isMobileViewport ? 'px-5' : 'px-8';
  const isSocialScan = visual === 'socialScan';
  const activeStage = progress.stages.find((stage) => stage.status === 'active');
  const statusLine = activeStage?.label
    ? `${activeStage.label}…`
    : isSocialScan
      ? 'Scanning your post…'
      : 'Reading document…';

  return (
    <div
      className={`trip-import-progress-step flex flex-col overflow-hidden ${
        isMobileViewport ? 'min-h-0 flex-1' : 'shrink-0'
      }`}
    >
      <header className={`flex flex-col gap-2 ${horizontalPad} ${isMobileViewport ? 'pt-5' : 'pt-7'}`}>
        <h2 className="font-lora text-[22px] font-semibold tracking-[0.3px] text-[#252528] min-[768px]:text-[28px]">
          Importing places
        </h2>
        <p className="font-manrope text-[14px] font-normal leading-5 tracking-[0.28px] text-[#525257]">
          {isUploading
            ? 'Uploading your file…'
            : 'Turning your itinerary into an organized, bookable trip.'}
        </p>
      </header>

      <div className={`${horizontalPad} pt-4`}>
        <div
          className="h-[4px] w-full overflow-hidden rounded-[4px] bg-[#e1e1e7]"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress.progressPercent}
        >
          <div
            className="h-full rounded-[4px] bg-[#ff5212] transition-[width] duration-300 ease-out"
            style={{ width: `${Math.max(progress.progressPercent, 4)}%` }}
          />
        </div>
      </div>

      <div
        className={`flex min-h-0 flex-1 gap-6 ${horizontalPad} py-7 ${
          isMobileViewport ? 'flex-col' : 'flex-row'
        }`}
      >
        {isSocialScan ? (
          <div className="flex shrink-0 flex-col items-center gap-3 text-center min-[768px]:w-[340px] min-[768px]:max-w-[340px]">
            <div
              className="relative h-[240px] w-full min-[768px]:h-[297px]"
              aria-hidden
            >
              <SocialParseAnimation
                className="h-full w-full"
                visibleFraction={0.85}
                fallbackText="Scanning Reel For Spots..."
              />
            </div>
            <div className="flex w-full flex-col items-center gap-1 px-2">
              <p className="font-manrope text-[14px] font-semibold leading-5 tracking-[0.28px] text-[#252528]">
                {statusLine}
              </p>
              <p className="font-manrope text-[12px] font-normal tracking-[0.24px] text-[#a7a7ae]">
                This may take up to a minute…
              </p>
            </div>
          </div>
        ) : (
          <ul className="flex shrink-0 flex-col gap-5 min-[768px]:w-[340px] min-[768px]:max-w-[340px]" aria-live="polite">
            {progress.stages.map((stage) => (
              <StageRow key={stage.id} stage={stage} foundCount={foundCount} />
            ))}
          </ul>
        )}

        {!isMobileViewport ? <div className="w-px shrink-0 self-stretch bg-[#e1e1e7]" aria-hidden /> : null}

        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3.5 min-[768px]:min-h-[220px]">
          <p className="font-manrope text-[14px] font-semibold leading-5 tracking-[0.28px] text-[#252528]">
            Found so far
          </p>
          {visible.length === 0 ? (
            <p className="font-manrope text-[14px] font-normal leading-5 tracking-[0.28px] text-[#a7a7ae]">
              Places will appear when import finishes.
            </p>
          ) : (
            <ul className="flex flex-col gap-2 overflow-y-auto">
              {visible.map((place, index) => (
                <FoundPlacePreviewRow
                  key={place.client_id}
                  place={place}
                  faded={place.row_kind === 'travel' && index === visible.length - 1 && extra > 0}
                />
              ))}
              {extra > 0 ? (
                <li className="pl-3 font-manrope text-[14px] font-semibold leading-5 tracking-[0.28px] text-[#ff5212]">
                  + {extra} more
                </li>
              ) : null}
            </ul>
          )}
        </div>
      </div>

      <footer
        className={`flex shrink-0 items-center gap-3 ${horizontalPad} ${
          isSocialScan ? 'justify-end' : 'justify-between'
        } ${
          isMobileViewport ? 'pb-[calc(1.25rem+env(safe-area-inset-bottom))] pt-4' : 'pb-7 pt-6'
        }`}
      >
        {!isSocialScan ? (
          <p className="font-manrope text-[12px] font-normal tracking-[0.24px] text-[#a7a7ae]">
            This may take up to a minute…
          </p>
        ) : null}
        <button
          type="button"
          className="inline-flex h-11 items-center justify-center rounded-full px-6 font-manrope text-[16px] font-semibold leading-[22px] tracking-[0.32px] text-[#525257] hover:bg-[#f3f3f3]"
          onClick={onCancel}
        >
          Cancel
        </button>
      </footer>
    </div>
  );
}

function StageRow({
  stage,
  foundCount,
}: {
  stage: TripImportProgressStage;
  foundCount: number;
}) {
  const isPending = stage.status === 'pending';
  const isActive = stage.status === 'active';
  const isDone = stage.status === 'done';

  let label: ReactNode = stage.label;
  if (stage.id === 'finding_places' && foundCount > 0 && (isActive || isDone)) {
    label = (
      <>
        <span className="font-normal text-[#525257]">Finding places — </span>
        <span className="font-semibold text-[#252528]">{foundCount} found</span>
      </>
    );
  }

  return (
    <li className="flex items-center gap-3.5">
      <StageStatusIcon status={stage.status} />
      <span
        className={`min-w-0 font-manrope text-[14px] leading-5 tracking-[0.28px] min-[768px]:max-w-[280px] ${
          isPending
            ? 'font-normal text-[#a7a7ae]'
            : isActive
              ? 'font-semibold text-[#252528]'
              : 'font-normal text-[#525257]'
        }`}
      >
        {label}
      </span>
    </li>
  );
}

function StageStatusIcon({ status }: { status: TripImportProgressStage['status'] }) {
  if (status === 'done') {
    return (
      <span
        className="inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-[#e3f0ad]"
        aria-hidden
      >
        <svg width={14} height={14} viewBox="0 0 14 14" fill="none">
          <path
            d="M2.5 7.5L5.5 10.5L11.5 3.5"
            stroke="#29453e"
            strokeWidth={1.8}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    );
  }
  if (status === 'active') {
    return (
      <span
        className="inline-flex size-7 shrink-0 items-center justify-center rounded-full border-2 border-[#e1e1e7] bg-white"
        aria-hidden
      >
        <span className="inline-flex size-3.5 animate-spin rounded-full border-2 border-[#e1e1e7] border-t-[#ff5212]" />
      </span>
    );
  }
  return (
    <span
      className="inline-block size-7 shrink-0 rounded-full border-2 border-[#e1e1e7] bg-white"
      aria-hidden
    />
  );
}

function FoundPlacePreviewRow({
  place,
  faded,
}: {
  place: TripImportFoundPlacePreview;
  faded?: boolean;
}) {
  const kind = iconKindForPreview(place);
  const isTravel = place.row_kind === 'travel';
  const iconBg = place.is_bookable ? 'bg-[#e3f0ad]' : isTravel ? 'bg-[#e1e1e7]' : 'bg-[#ffdbc6]';

  return (
    <li
      className={`flex items-center gap-2.5 rounded-[12px] border border-[#e1e1e7] px-3 py-2.5 ${
        faded || isTravel ? 'opacity-45' : ''
      }`}
    >
      <span
        className={`inline-flex size-6 shrink-0 items-center justify-center rounded-full ${iconBg}`}
        aria-hidden
      >
        {place.is_bookable ? (
          <svg width={14} height={14} viewBox="0 0 14 14" fill="none">
            <path
              d="M2.5 7.5L5.5 10.5L11.5 3.5"
              stroke="#29453e"
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          <PlaceSearchRowTypeIcon kind={kind} />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-manrope text-[14px] font-semibold leading-5 tracking-[0.28px] text-[#252528]">
          {place.name}
        </p>
        {place.subtitle ? (
          <p className="truncate font-manrope text-[12px] font-normal tracking-[0.24px] text-[#525257]">
            {place.subtitle}
          </p>
        ) : null}
      </div>
      {place.is_bookable ? (
        <span className="shrink-0 rounded-full bg-[#e3f0ad] px-2.5 py-1 font-manrope text-[11px] font-semibold tracking-[0.22px] text-[#29453e]">
          Bookable
        </span>
      ) : null}
    </li>
  );
}

function iconKindForPreview(place: TripImportFoundPlacePreview): PlaceSearchRowSlotKind {
  if (place.row_kind === 'travel') return 'flight';
  switch (place.category) {
    case 'stay':
      return 'stay';
    case 'eat':
      return 'eat';
    case 'flight':
      return 'flight';
    default:
      return 'do';
  }
}
